import React, { useEffect, useState } from "react";
import { useAuth } from "@/auth";
import { useI18n } from "@/i18n";
import { useNavigate } from "react-router-dom";
import { Calculator, Route as RouteIcon, Ruler, Users, Package, Truck, MapPin, Clock, CheckCircle2, Warehouse, X, Mail, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  recibido: "bg-slate-100 text-slate-700",
  despacho: "bg-blue-100 text-red-700",
  en_aduana: "bg-amber-100 text-amber-800",
  en_reparto: "bg-indigo-100 text-indigo-700",
  entregado: "bg-emerald-100 text-emerald-700",
};

export default function Dashboard() {
  const { user, api } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [stats, setStats] = useState({ users: 0, routes: 0, clientes: 0 });
  const [cfg, setCfg] = useState({ vol: { aereo: 6000, maritimo: 1000 } });
  const [shipments, setShipments] = useState([]);
  const [labels, setLabels] = useState([]);
  const [profile, setProfile] = useState(null);
  const [showProfilePrompt, setShowProfilePrompt] = useState(false);
  const [emailHealth, setEmailHealth] = useState(null);
  const [inbound, setInbound] = useState(null);

  useEffect(() => {
    api.get("/stats").then(r => setStats(r.data)).catch(()=>{});
    api.get("/config").then(r => setCfg(r.data)).catch(()=>{});
    if (["cliente", "sucursal"].includes(user.role)) {
      api.get("/shipments").then(r => setShipments(r.data)).catch(()=>{});
    }
    if (user.role === "cliente") {
      api.get("/labels").then(r => setLabels(r.data || [])).catch(()=>{});
      api.get("/auth/me/full").then(r => {
        setProfile(r.data);
        const dismissed = localStorage.getItem(`meos_profile_prompt_${user.id}`);
        if (!r.data.provincia && !dismissed) setShowProfilePrompt(true);
      }).catch(()=>{});
    }
    if (user.role === "admin") {
      api.get("/admin/email-log").then(r => setEmailHealth(r.data)).catch(()=>{});
    }
    if (["admin", "sucursal", "operaciones"].includes(user.role)) {
      api.get("/dashboard/inbound").then(r => setInbound(r.data)).catch(()=>{});
    }
  // eslint-disable-next-line
  }, []);

  const dismissProfilePrompt = () => {
    localStorage.setItem(`meos_profile_prompt_${user.id}`, "1");
    setShowProfilePrompt(false);
  };

  // ============ SUCURSAL DASHBOARD ============
  if (user.role === "sucursal") {
    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();
    const isThisMonth = (iso) => {
      if (!iso) return false;
      const d = new Date(iso);
      return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
    };
    const pendientes = shipments.filter(s => ["recibido", "despacho"].includes(s.estado)).length;
    const enAduana = shipments.filter(s => s.estado === "en_aduana").length;
    const enReparto = shipments.filter(s => s.estado === "en_reparto").length;
    const entregadosMes = shipments.filter(s => s.estado === "entregado" && isThisMonth(s.updated_at || s.created_at)).length;

    const Card = ({ Icon, label, value, tone }) => (
      <div className={`border rounded-xl p-5 hover-lift bg-white`}>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${tone}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className="font-display text-3xl font-bold tabular-nums text-slate-900 mt-1">{value}</div>
      </div>
    );

    const upcoming = shipments.slice(0, 5);

    return (
      <div className="space-y-6" data-testid="sucursal-dashboard">
        <div className="rounded-2xl bg-gradient-to-r from-fuchsia-600 to-purple-700 text-white p-6 shadow-lg flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Warehouse className="w-8 h-8" />
            <div>
              <h2 className="font-display text-2xl font-bold">{user.fullname}</h2>
              <p className="opacity-90 text-sm">{t("assigned_office")}: <b>{user.oficina || "—"}</b></p>
            </div>
          </div>
          <button onClick={() => nav("/envios")} className="bg-white text-purple-700 font-semibold px-4 py-2 rounded-lg hover:bg-purple-50 flex items-center gap-2">
            <Package className="w-4 h-4" /> {t("shipments")} ({shipments.length})
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card Icon={Clock} label={t("pending")} value={pendientes} tone="bg-slate-100 text-slate-700" />
          <Card Icon={Truck} label={t("status_en_aduana")} value={enAduana} tone="bg-amber-100 text-amber-700" />
          <Card Icon={MapPin} label={t("status_en_reparto")} value={enReparto} tone="bg-indigo-100 text-indigo-700" />
          <Card Icon={CheckCircle2} label={t("delivered_this_month")} value={entregadosMes} tone="bg-emerald-100 text-emerald-700" />
        </div>

        {inbound && inbound.inbound_masters > 0 && (
          <button
            onClick={() => nav("/masters")}
            data-testid="dash-inbound-card"
            className="w-full text-start rounded-2xl border-2 border-red-300 bg-gradient-to-r from-red-50 to-white p-5 hover:shadow-md transition-shadow flex items-center gap-4"
          >
            <div className="w-14 h-14 rounded-xl bg-red-600 text-white flex items-center justify-center shrink-0">
              <Truck className="w-7 h-7" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-bold uppercase tracking-widest text-red-700">{t("inbound_masters")}</div>
              <div className="font-display text-2xl font-bold text-slate-900 mt-0.5">
                {inbound.inbound_masters} <span className="text-sm font-medium text-slate-500">→ {inbound.inbound_packages} {t("inbound_packages_hint")}</span>
              </div>
            </div>
            <span className="text-red-600 font-semibold text-sm">{t("view_details")} →</span>
          </button>
        )}

        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-display font-semibold">{t("recent_shipments")}</h3>
            <button onClick={() => nav("/envios")} className="text-xs text-red-600 font-semibold hover:underline">{t("view_details")} →</button>
          </div>
          {upcoming.length === 0 ? (
            <p className="text-slate-500 text-sm p-4">{t("no_shipments_admin")}</p>
          ) : (
            <div className="space-y-2">
              {upcoming.map(s => (
                <div key={s.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="min-w-0">
                    <div className="font-mono text-sm font-semibold">{s.tracking_code}</div>
                    <div className="text-xs text-slate-500 truncate">{s.origen} → {s.destino} · {s.cliente_nombre || ""}</div>
                  </div>
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-md ${STATUS_COLORS[s.estado]}`}>
                    {t(`status_${s.estado}`)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ============ CLIENTE DASHBOARD ============
  if (user.role === "cliente") {
    return (
      <div className="space-y-5" data-testid="dashboard">
        {showProfilePrompt && (
          <CompleteProfileBanner
            profile={profile}
            onSaved={(p) => { setProfile(p); setShowProfilePrompt(false); }}
            onDismiss={dismissProfilePrompt}
            api={api}
            t={t}
          />
        )}
        <div className="rounded-2xl bg-gradient-to-r from-red-600 to-red-700 text-white p-8 shadow-lg">
          <h2 className="font-display text-3xl font-bold mb-2">{t("welcome")}, {user.fullname}</h2>
          <p className="opacity-90 mb-6">{t("empty_quote_sub")}</p>
          <div className="flex gap-3 flex-wrap">
            <button data-testid="dash-cta-cotizar" onClick={() => nav("/cotizar")} className="bg-white text-red-700 font-semibold px-5 py-2.5 rounded-lg hover:bg-red-50 flex items-center gap-2">
              <Calculator className="w-4 h-4" /> {t("quote_shipping")}
            </button>
            <button onClick={() => nav("/envios")} className="bg-red-800 text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-red-900 flex items-center gap-2">
              <Package className="w-4 h-4" /> {t("my_shipments")} ({shipments.length + labels.length})
            </button>
          </div>
        </div>
        {labels.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="dash-my-labels">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-display font-semibold flex items-center gap-2">
                <Package className="w-4 h-4 text-red-600" /> {t("my_registered_packages") || "Mis paquetes registrados"}
              </h3>
              <button onClick={() => nav("/envios")} className="text-xs text-red-600 font-semibold hover:underline">{t("view_details")} →</button>
            </div>
            <div className="space-y-2">
              {labels.slice(0, 5).map(l => {
                const isIncoming = (l.receptor_email || "").toLowerCase() === (user.email || "").toLowerCase();
                return (
                <button
                  key={l.id}
                  data-testid={`dash-lbl-${l.codigo}`}
                  onClick={() => nav(`/track/${encodeURIComponent(l.codigo)}`)}
                  className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-lg text-start transition-colors"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-semibold text-slate-900">{l.codigo}</span>
                      {isIncoming && (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700" data-testid={`dash-incoming-${l.codigo}`}>
                          {t("incoming_shipment") || "Entrante"}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {[l.origen_ciudad, l.origen_pais].filter(Boolean).join(", ") || "—"} → {[l.destino_ciudad, l.destino_pais].filter(Boolean).join(", ") || "—"}
                    </div>
                  </div>
                  <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-md bg-amber-100 text-amber-700">
                    {t(`flow_${l.estado_flujo || "clasificado"}`) || (l.estado_flujo || "clasificado")}
                  </span>
                </button>
                );
              })}
            </div>
          </div>
        )}
        {shipments.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-display font-semibold">{t("my_shipments")}</h3>
              <button onClick={() => nav("/envios")} className="text-xs text-red-600 font-semibold hover:underline">{t("view_details")} →</button>
            </div>
            <div className="space-y-2">
              {shipments.slice(0,3).map(s => (
                <div key={s.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <div className="font-mono text-sm font-semibold">{s.tracking_code}</div>
                    <div className="text-xs text-slate-500">{s.origen} → {s.destino}</div>
                  </div>
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-md ${STATUS_COLORS[s.estado]}`}>{t(`status_${s.estado}`)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ============ ADMIN / VENTAS / OPS ============
  const Stat = ({ label, value, Icon }) => (
    <div className="bg-white border border-slate-200 rounded-xl p-5 hover-lift">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</span>
        <Icon className="w-4 h-4 text-slate-400" />
      </div>
      <div className="font-display text-3xl font-bold tabular-nums text-slate-900">{value}</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="dashboard">
      {user.role === "admin" && emailHealth && emailHealth.configured && !emailHealth.domain_verified && (
        <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="dash-email-banner">
          <div className="w-9 h-9 rounded-lg bg-amber-500 text-white flex items-center justify-center shrink-0">
            <Mail className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <AlertTriangle className="w-4 h-4 text-amber-700" />
              <h4 className="font-semibold text-sm text-amber-900">{t("email_why_failing")}</h4>
            </div>
            <p className="text-xs text-amber-800 mt-1">{t("email_fix_steps")}</p>
            <div className="flex items-center gap-2 flex-wrap mt-2">
              <span className="text-[11px] text-amber-700">{t("email_sender") || "Remitente"}: <span className="font-mono">{emailHealth.sender}</span></span>
              <button
                onClick={() => nav("/backups")}
                data-testid="dash-email-banner-open"
                className="text-[11px] bg-amber-600 hover:bg-amber-700 text-white font-semibold px-3 py-1 rounded"
              >
                {t("email_open_diagnostics") || "Abrir diagnóstico"}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label={t("total_users")} value={stats.users} Icon={Users} />
        <Stat label={t("active_routes")} value={stats.routes} Icon={RouteIcon} />
        <Stat label={t("air_divisor")} value={`/${cfg.vol?.aereo || 6000}`} Icon={Ruler} />
        <Stat label={t("sea_divisor")} value={`/${cfg.vol?.maritimo || 1000}`} Icon={Ruler} />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h3 className="font-display font-semibold text-lg mb-4">{t("quick_access")}</h3>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => nav("/cotizar")} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2">
            <Calculator className="w-4 h-4" /> {t("quote_shipping")}
          </button>
          {user.role === "admin" && (
            <>
              <button onClick={() => nav("/rutas")} className="bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2">
                <RouteIcon className="w-4 h-4" /> {t("own_routes")}
              </button>
              <button onClick={() => nav("/volumen")} className="border border-slate-300 hover:border-slate-500 px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2">
                <Ruler className="w-4 h-4" /> {t("volumetric_rules")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function CompleteProfileBanner({ profile, onSaved, onDismiss, api, t }) {
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({
    tipo: profile?.tipo || "privado",
    telefono: profile?.telefono || "",
    empresa: profile?.empresa || "",
    direccion: profile?.direccion || "",
    ciudad: profile?.ciudad || "",
    provincia: profile?.provincia || "",
    pais: profile?.pais || "",
    codigo_postal: profile?.codigo_postal || "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inp = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-red-600 focus:ring-2 focus:ring-red-100";
  const lbl = "block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide";

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.patch("/auth/me", form);
      toast.success(t("profile_updated"));
      onSaved(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  if (!expanded) {
    return (
      <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="profile-prompt">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <MapPin className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="font-semibold text-amber-900 text-sm">{t("complete_profile")}</div>
            <div className="text-xs text-amber-800/80 mt-0.5">{t("complete_profile_desc")}</div>
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            onClick={() => setExpanded(true)}
            data-testid="profile-prompt-open"
            className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-xs font-semibold transition"
          >
            {t("complete_profile")}
          </button>
          <button
            onClick={onDismiss}
            data-testid="profile-prompt-dismiss"
            className="text-amber-700 hover:bg-amber-100 p-2 rounded-lg transition"
            aria-label={t("skip_for_now")}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white border-2 border-amber-300 rounded-xl p-5 space-y-3" data-testid="profile-form">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-semibold text-base flex items-center gap-2">
          <MapPin className="w-5 h-5 text-amber-600" /> {t("complete_profile")}
        </h3>
        <button type="button" onClick={onDismiss} className="text-slate-400 hover:text-slate-700">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className={lbl}>{t("client_type")}</label>
          <select value={form.tipo} onChange={(e) => set("tipo", e.target.value)} className={inp}>
            <option value="privado">{t("private")}</option>
            <option value="empresa">{t("company")}</option>
          </select>
        </div>
        <div>
          <label className={lbl}>{t("phone")}</label>
          <input value={form.telefono} onChange={(e) => set("telefono", e.target.value)} className={inp} />
        </div>
        {form.tipo === "empresa" && (
          <div className="sm:col-span-2">
            <label className={lbl}>{t("company")}</label>
            <input value={form.empresa} onChange={(e) => set("empresa", e.target.value)} className={inp} />
          </div>
        )}
        <div className="sm:col-span-2">
          <label className={lbl}>{t("address")}</label>
          <input value={form.direccion} onChange={(e) => set("direccion", e.target.value)} className={inp} />
        </div>
        <div>
          <label className={lbl}>{t("city")}</label>
          <input value={form.ciudad} onChange={(e) => set("ciudad", e.target.value)} className={inp} />
        </div>
        <div>
          <label className={lbl}>{t("province")}</label>
          <input required data-testid="profile-province" value={form.provincia} onChange={(e) => set("provincia", e.target.value)} className={inp} />
        </div>
        <div>
          <label className={lbl}>{t("country")}</label>
          <input value={form.pais} onChange={(e) => set("pais", e.target.value)} className={inp} />
        </div>
        <div>
          <label className={lbl}>{t("postal_code")}</label>
          <input value={form.codigo_postal} onChange={(e) => set("codigo_postal", e.target.value)} className={inp} />
        </div>
      </div>
      <div className="flex gap-2 justify-end pt-2">
        <button type="button" onClick={onDismiss} className="border border-slate-300 px-4 py-2 rounded-lg text-xs font-semibold hover:bg-slate-50">
          {t("skip_for_now")}
        </button>
        <button type="submit" data-testid="profile-save" className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-xs font-semibold">
          {t("save_profile")}
        </button>
      </div>
    </form>
  );
}

